import React, { useMemo } from "react";
import Highcharts from "../../config/highcharts-config";
import HighchartsReact from "highcharts-react-official";
import { useAppSelector } from "@store/index";
import { donutChartConfigSelector } from "@store/slices/donut-chart-config-slice";
import { useDonutChartOptions } from "@hooks/use-donut-chart-options";

// Highcharts modules (Exporting, Accessibility) are now initialized globally in highcharts-config.ts
// This provides hamburger menu functionality across all chart components

const ConfigurableDonutChart: React.FC = () => {
  const { currentConfig } = useAppSelector(donutChartConfigSelector);

  // Apply grouping and sorting to chart data
  const chartData = useMemo(() => {
    const rawData = currentConfig.series[0]?.data || [];
    let processedData = rawData.map((item) => ({ ...item }));

    // Apply grouping logic
    if (currentConfig.groups && currentConfig.groups.length > 0) {
      const groupedData = [...processedData];
      const usedSliceNames = new Set<string>();

      // Process each group
      currentConfig.groups.forEach((group) => {
        if (group.sliceNames.length >= 2 && group.name.trim()) {
          // Calculate group total
          const groupTotal = group.sliceNames.reduce((sum, sliceName) => {
            const slice = rawData.find((s) => s.name === sliceName);
            return sum + (slice?.y || 0);
          }, 0);

          if (group.showAsSlice) {
            // Replace individual slices with group slice
            group.sliceNames.forEach((sliceName) => {
              usedSliceNames.add(sliceName);
              const index = groupedData.findIndex((s) => s.name === sliceName);
              if (index !== -1) {
                groupedData.splice(index, 1);
              }
            });

            // Add group slice
            groupedData.push({
              name: group.name,
              y: groupTotal,
              color: group.sliceNames[0]
                ? rawData.find((s) => s.name === group.sliceNames[0])?.color
                : "#57bfe7",
              isGroup: true,
              groupId: group.id,
            });
          } else {
            // Keep individual slices, just mark them as part of a group
            group.sliceNames.forEach((sliceName) => {
              const slice = groupedData.find((s) => s.name === sliceName);
              if (slice) {
                slice.groupId = group.id;
                slice.groupName = group.name;
              }
            });
          }
        }
      });

      processedData = groupedData;
    }

    // Apply sorting
    if (currentConfig.dataSorting === "ascending") {
      return processedData.sort((a, b) => a.y - b.y);
    } else if (currentConfig.dataSorting === "descending") {
      return processedData.sort((a, b) => b.y - a.y);
    }

    // Return original order for "none" or undefined
    return processedData;
  }, [currentConfig.series, currentConfig.dataSorting, currentConfig.groups]);

  // Use the shared donut chart options hook
  const options = useDonutChartOptions({
    config: currentConfig,
    chartData,
    isConfigPreview: true, // This is the config preview component
  });

  return (
    <div style={{ width: "100%", height: "100%", minHeight: 400 }}>
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        containerProps={{ style: { width: "100%", height: "100%" } }}
        label="Configurable Donut Chart"
      />
    </div>
  );
};

export default ConfigurableDonutChart;
